import React from 'react';
import { Box, Typography, Button, Paper, Avatar, Chip, Divider } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Person as PersonIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  PUBLISHER: 'Publisher',
  CLIENT: 'Client',
};

const ProfileSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const initials = user
    ? `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase() || (user.email || '').charAt(0).toUpperCase()
    : '';

  const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1.75, px: 0.5 }}>
      <Typography sx={{ width: 160, color: '#64748b', fontSize: 14, fontWeight: 500, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#1e293b', fontSize: 14 }}>{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/settings')}
        sx={{ textTransform: 'none', color: '#64748b', mb: 2, '&:hover': { color: '#1e293b' } }}
      >
        Back to Settings
      </Button>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
          Profile Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          View your account details
        </Typography>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 720 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#3b82f6', fontSize: 22, fontWeight: 600 }}>
            {initials || <PersonIcon />}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#1e293b' }}>
              {fullName || '—'}
            </Typography>
            <Chip
              label={roleLabels[user?.role || ''] || user?.role || '—'}
              size="small"
              sx={{ mt: 0.5, bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Row label="First Name" value={user?.firstName || '—'} />
          <Divider />
          <Row label="Last Name" value={user?.lastName || '—'} />
          <Divider />
          <Row label="Email" value={user?.email || '—'} />
          <Divider />
          <Row label="Role" value={roleLabels[user?.role || ''] || user?.role || '—'} />
        </Box>
      </Paper>
    </Box>
  );
};

export default ProfileSettingsPage;
